import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { map, Observable } from 'rxjs';
import {
	DefaultErrorMessage,
	mapResponse,
} from 'src/lib/utilities/api/map-response';
import { PagingArgument } from 'src/lib/utilities/api/patterns/pagination/paging.argument';

import { buildQueryString } from 'src/lib/utilities/api/http';
import { CurriculumManagerHistoryResponseModel } from '../curriculum-manager-history.model';
import { CurriculumCatalogBaseSubjectAreaModel } from './curriculum-catalog-base-subject-area.model';
import {
	CatalogUpsertSubjectAreaArgument,
	CurriculumCatalogModel,
	CurriculumCatalogSearchArgument,
	CurriculumCatalogSearchResultModel,
	CurriculumCatalogSectionModel,
	CurriculumCatalogSubjectAreaModel,
	CurriculumCatalogUpsertArgument,
} from './curriculum-catalog.model';

@Injectable({
	providedIn: 'root',
})
export class CurriculumCatalogService {
	constructor(private httpClient: HttpClient) {}
	public searchCatalogs = (
		args: PagingArgument<CurriculumCatalogSearchArgument>,
	): Observable<CurriculumCatalogSearchResultModel> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/catalog/search`, args)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumCatalogSearchResultModel, r),
					{
						errorCode: '37F08116',
					},
				),
			);
	};

	public viewCatalog = (id: number): Observable<CurriculumCatalogModel> => {
		return this.httpClient
			.get<{ catalog: unknown }>(`/api/v1/curriculum/catalog/${id}`)
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCatalogModel, r.catalog), {
					errorCode: 'A2D3D3AB',
				}),
			);
	};

	public viewBaseSubjectAreas = (): Observable<
		CurriculumCatalogBaseSubjectAreaModel[]
	> => {
		return this.httpClient
			.get<{
				subject_areas: unknown[];
			}>(`/api/v1/curriculum/catalog/base_subject_areas`)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(
							CurriculumCatalogBaseSubjectAreaModel,
							r.subject_areas,
						),
					{
						errorCode: 'A2D3D3AB',
					},
				),
			);
	};

	public createCatalog = (
		args: Partial<CurriculumCatalogUpsertArgument>,
	): Observable<CurriculumCatalogModel> => {
		return this.httpClient
			.put<{ catalog: unknown }>(`/api/v1/curriculum/catalog`, args)
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCatalogModel, r.catalog), {
					errorCode: '912A4D8B',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public editCatalog = (
		id: number,
		args: Partial<CurriculumCatalogUpsertArgument>,
	): Observable<CurriculumCatalogModel> => {
		return this.httpClient
			.post<{ catalog: unknown }>(`/api/v1/curriculum/catalog/${id}`, args)
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCatalogModel, r.catalog), {
					errorCode: '912A4D8B',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Edit Catalog in postman
	public reactivateCatalog = (id: number): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(`/api/v1/curriculum/catalog/${id}?reactivate=TRUE`, {})
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'D61C14A2',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	// Delete Catalog in postman
	public deactivateCatalog = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/catalog/${id}?delete=FALSE`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '2CD800E7',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public deleteCatalog = (id: number): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(`/api/v1/curriculum/catalog/${id}`)
			.pipe(
				mapResponse((r) => r, {
					errorCode: 'D1078A41',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public editCatalogSection = (
		catalogId: number,
		sectionId: number,
		suggestion_rank: number | null,
	): Observable<CurriculumCatalogModel> => {
		return this.httpClient
			.post<{
				catalog: unknown;
			}>(`/api/v1/curriculum/catalog/${catalogId}/section/${sectionId}`, {
				suggestion_rank,
			})
			.pipe(
				mapResponse((r) => plainToInstance(CurriculumCatalogModel, r.catalog), {
					errorCode: '0D9A4102',
				}),
			);
	};

	public viewSuggestedSections = (
		id: number,
	): Observable<CurriculumCatalogSectionModel[]> => {
		return this.httpClient
			.get<{
				sections: unknown[];
			}>(`/api/v1/curriculum/catalog/${id}/suggested_sections`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumCatalogSectionModel, r.sections),
					{
						errorCode: 'A964D6B7',
					},
				),
			);
	};

	public viewHistory = (
		id: number,
		start_date: string,
		end_date: string,
		skip: string = '0',
		take: string = '1000',
	): Observable<CurriculumManagerHistoryResponseModel> => {
		let qs = buildQueryString({
			skip,
			take,
		});

		if (start_date && end_date) {
			qs = buildQueryString({
				start_date,
				end_date,
				skip,
				take,
			});
		}
		return this.httpClient
			.get<unknown>(`/api/v1/curriculum/catalog/${id}/history${qs}`)
			.pipe(
				mapResponse(
					(r) => plainToInstance(CurriculumManagerHistoryResponseModel, r),
					{
						errorCode: '98E57940',
					},
				),
			);
	};

	/** Subject Areas  **/
	public addSubjectArea = (
		catalog_id: number,
		args: CatalogUpsertSubjectAreaArgument,
	): Observable<CurriculumCatalogSubjectAreaModel> => {
		return this.httpClient
			.put<{
				subject_area: unknown;
			}>(`/api/v1/curriculum/catalog/${catalog_id}/subject_area`, args)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(CurriculumCatalogSubjectAreaModel, r.subject_area),
					{
						errorCode: '6A68DC32',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};
	public editSubjectArea = (
		id: number,
		catalog_id: number,
		args: CatalogUpsertSubjectAreaArgument,
	): Observable<CurriculumCatalogSubjectAreaModel> => {
		return this.httpClient
			.post<{
				subject_area: unknown;
			}>(`/api/v1/curriculum/catalog/${catalog_id}/subject_area/${id}`, args)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(CurriculumCatalogSubjectAreaModel, r.subject_area),
					{
						errorCode: '4DAF083C',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};

	public deleteSubjectArea = (
		id: number,
		catalog_id: number,
	): Observable<unknown> => {
		return this.httpClient
			.delete<unknown>(
				`/api/v1/curriculum/catalog/${catalog_id}/subject_area/${id}`,
			)
			.pipe(
				mapResponse((r) => r, {
					errorCode: '0587834B',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public overrideCourseSubjectArea = (
		course_id: number,
		catalog_id: number,
		subject_area_ids: number[],
	): Observable<unknown> => {
		return this.httpClient
			.post<unknown>(
				`/api/v1/curriculum/catalog/${catalog_id}/course/${course_id}`,
				{
					subject_area_ids,
				},
			)
			.pipe(
				map(() => null), // response is empty string or something causing front end to think its a error so mapping response to null
				mapResponse((r) => r, {
					errorCode: 'EAE33E06',
					errorMessage: DefaultErrorMessage.Saving,
				}),
			);
	};

	public createBaseSubjectArea = (
		title: string,
		short_code: string,
	): Observable<CurriculumCatalogBaseSubjectAreaModel> => {
		return this.httpClient
			.put<{ subject_area: unknown }>(
				`/api/v1/curriculum/catalog/base_subject_areas`,
				{
					title,
					short_code,
				},
			)
			.pipe(
				mapResponse(
					(r) =>
						plainToInstance(
							CurriculumCatalogBaseSubjectAreaModel,
							r.subject_area,
						),
					{
						errorCode: '7834E036',
						errorMessage: DefaultErrorMessage.Saving,
					},
				),
			);
	};
}
